import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, DollarSign, Check } from 'lucide-react';
import { API_BASE_URL } from '../../../config';
import './ModuleServices.css';

const ModuleServices = ({ module, onSelectService, onBack }) => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchServices = async () => {
            if (!module || !module.id) return;

            try {
                setLoading(true);
                setError(null);

                const response = await fetch(`${API_BASE_URL}/barber/service-modules/${module.id}/services`);

                if (!response.ok) {
                    throw new Error('Error al cargar los servicios');
                }

                const data = await response.json();
                setServices(data);
            } catch (err) {
                console.error('Error fetching services:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchServices();
    }, [module]);

    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(price);
    };

    const getModuleColor = (moduleName) => {
        switch (moduleName) {
            case 'standard':
                return '#3b82f6';
            case 'proyeccion':
                return '#10b981';
            case 'pro':
                return '#f59e0b';
            case 'andersson':
                return '#8b5cf6'; // Púrpura/Violeta
            default:
                return '#6b7280';
        }
    };

    if (loading) {
        return (
            <div className="module-services-loading">
                <div className="spinner"></div>
                <p>Cargando servicios...</p>
            </div>
        );
    }

    return (
        <div className="module-services">
            {/* Header con botón de volver */}
            <div className="module-services-header">
                <button className="back-button" onClick={onBack}>
                    <ArrowLeft size={20} />
                    <span>Volver a módulos</span>
                </button>
                <div className="module-services-title">
                    <h2>{module.display_name}</h2>
                    {module.description && (
                        <p className="module-description">{module.description}</p>
                    )}
                </div>
            </div>

            {/* Imagen del menú si existe */}
            {module.image_url && (
                <div className="module-menu-image">
                    <img src={module.image_url} alt={`Menú ${module.display_name}`} />
                </div>
            )}

            {/* Lista de servicios */}
            {error ? (
                <div className="module-services-error">
                    <p>Error al cargar los servicios: {error}</p>
                </div>
            ) : services.length === 0 ? (
                <div className="module-services-empty">
                    <p>No hay servicios disponibles para este módulo</p>
                </div>
            ) : (
                <div className="services-grid">
                    {services.map((service, index) => (
                        <motion.div
                            key={service.id}
                            className="service-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            onClick={() => onSelectService(service)}
                            style={{ 
                                '--module-color': getModuleColor(module.name),
                                cursor: 'pointer'
                            }}
                        >
                            <div className="service-card-header">
                                <h3 className="service-name">{service.name}</h3>
                            </div>

                            <div className="service-card-body">
                                <div className="service-info">
                                    <div className="service-price">
                                        <DollarSign size={20} />
                                        <span>{formatPrice(service.price)}</span>
                                    </div>
                                    <div className="service-duration">
                                        <Clock size={16} />
                                        <span>{service.duration_minutes} min</span>
                                    </div>
                                </div>
                            </div>

                            <div className="service-card-footer">
                                <span>Seleccionar servicio</span>
                                <Check size={18} />
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ModuleServices;

